'use client';

import { useMemo } from 'react';

import { useShowMoreLess } from '@/hooks/useShowMoreLess';
import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import Button, { ButtonProps } from '@/components/atoms/Button';
import { animatedIconRightStyles } from '@/components/atoms/Button/styles';
import { passButtonIcons } from '@/components/atoms/Button/utils';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/components/atoms/Image/ImageStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/components/atoms/RichTextField/RichTextFieldStrapi';
import { Text } from '@/components/atoms/Text';
import {
  ToggleCMSProps,
  useCMSToggle,
} from '@/components/atoms/Toggle/useCMSToggle';
import { SvgIcon } from '@/components/SvgIcon';

import { BlockHeader, BlockHeaderContent } from '@/cms/BlockHeaderContentDC';
import { BlockWrapper } from '@/cms/BlockWrapperDC';
import { BlockReferenceArray } from '@/cms/types/block';
import { BlockComponentModel } from '@/cms/types/blok-component';
import PostByLine from '@/features/blog/PostByLine';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';
import { formatDate } from '@/utils/formatDate';

import { SimpleCard } from '../SimpleCards';

import iconChevronRightSrc from '~/svg/IconChevronRight.svg';

export type SocialProofProps = {
  header: BlockHeader;
  toggle?: ToggleCMSProps;
  reviews?: BlockReferenceArray<{
    title: string;
    description: RichTextFieldStrapiProps;
    image: ImageStrapiProps;
    date?: string;
    url?: string;
    reviewUsername?: string;
    reviewDate?: string;
    isReadMoreVisible?: boolean;
    type: 'g2' | 'trustpilot' | 'awards' | 'industry-experts';
  }>;
  reviews_groups: {
    data: {
      id: number;
      attributes: {
        title: string;
        reviewElements?: {
          title: string;
          description: RichTextFieldStrapiProps;
          image: ImageStrapiProps;
          date?: string;
          url?: string;
          reviewUsername?: string;
          reviewDate?: string;
          isReadMoreVisible?: boolean;
          type: 'g2' | 'trustpilot' | 'awards' | 'industry-experts';
        }[];
      };
    }[];
  };
};

export function SocialProofStrapi({
  block,
  ...rest
}: BlockComponentModel<SocialProofProps>) {
  const { header, reviews, toggle, reviews_groups } = block;
  const { texts } = useTranslation();

  const cards = useMemo(() => {
    if (reviews_groups?.data?.length) {
      return reviews_groups.data.flatMap((group) => {
        if (!group.attributes.reviewElements) return [];

        return group.attributes.reviewElements.map((review) => ({
          title: review.title,
          description: review.description,
          image: review.image,
          reviewUsername: review.reviewUsername,
          reviewDate: review.reviewDate,
          url: review.url,
          isReadMoreVisible: review.isReadMoreVisible,
        }));
      });
    }

    if (reviews?.data?.length) {
      return reviews.data.map(({ attributes }) => ({
        title: attributes.title,
        description: attributes.description,
        image: attributes.image,
        reviewUsername: attributes.reviewUsername,
        reviewDate: attributes.reviewDate,
        url: attributes.url,
        isReadMoreVisible: attributes.isReadMoreVisible,
      }));
    }

    return [];
  }, [reviews_groups, reviews]);

  const { filteredToggleList, toggleNode: ToggleNode } = useCMSToggle(
    cards,
    toggle
  );
  const selectedCards = filteredToggleList || cards;
  const { toggleShowMore, initiallyVisibleItems, buttonText, showAction } =
    useShowMoreLess({
      items: selectedCards,
      initialShowCount: 3,
    });

  function getSignatureFromFullname(fullname: string) {
    if (!fullname) return '';
    const fullnameArray = fullname.split(' ');

    if (fullnameArray.length === 1)
      return fullnameArray[0].charAt(0).toUpperCase();

    return (
      fullname.split(' ')[0].charAt(0).toUpperCase() +
      fullname.split(' ')[1].charAt(0).toUpperCase()
    );
  }

  return (
    <BlockWrapper {...rest} data-tracker-section={SECTION_NAMES.SOCIAL_PROOF}>
      <div className='flex h-full'>
        <BlockHeaderContent
          {...header}
          layoutOptions={{ constrainWidthWithPadding: true }}
          topChildren={
            ToggleNode ? ToggleNode({ responsive: true }) : undefined
          }
          classNames={{
            children: 'grid md:grid-cols-3 justify-normal md:justify-center',
          }}
          renderButtons={
            showAction
              ? [
                  (props: Partial<ButtonProps>) => (
                    <Button
                      onClick={toggleShowMore}
                      key={buttonText + 'social-proof-strapi'}
                      {...passButtonIcons(props)}
                      variant={props.variant || 'outlined'}
                      color={props.color || 'secondary'}
                    >
                      {buttonText}
                    </Button>
                  ),
                ]
              : undefined
          }
        >
          {cards.map(
            ({
              title,
              description,
              image,
              reviewDate,
              reviewUsername,
              url,
              isReadMoreVisible,
            }) => (
              <div
                className={`${cn(
                  'h-full w-full flex-1 md:w-full',
                  initiallyVisibleItems?.length &&
                    !initiallyVisibleItems.some(
                      (card) => card?.title === title
                    ) &&
                    'hide-content'
                )}`}
                key={title}
              >
                <SimpleCard
                  url={url}
                  classNames={{
                    root: cn('w-full relative self-stretch p-6 rounded h-full'),
                  }}
                >
                  <ImageStrapi
                    block={image}
                    inner={{
                      classNames: {
                        container: 'mb-3 h-12',
                        image: 'max-h-12 object-left',
                      },
                    }}
                    width={120}
                    height={24}
                  />
                  <Text
                    variant='subtitle-l-d'
                    color='gray-950'
                    classNames={{ wrapper: 'mb-4 line-clamp-2' }}
                  >
                    {title}
                  </Text>
                  <div
                    data-tracker-component='description'
                    className='md:min-h-24'
                  >
                    <div className='line-clamp-3'>
                      <RichTextFieldStrapi
                        block={description}
                        classNames={{
                          paragraph: 'text-gray-500',
                        }}
                      />
                    </div>
                  </div>

                  {/* SimpleCard already wraps the card in <a> when url is set; a nested <a> here breaks hydration (React #418) */}
                  {url && isReadMoreVisible && (
                    <span
                      className={cn(
                        'group inline-flex items-center gap-2 font-mono text-brand-500',
                        'md:absolute md:bottom-6 md:left-6'
                      )}
                    >
                      {texts.social_proof.read_more}
                      <SvgIcon
                        src={iconChevronRightSrc}
                        className={animatedIconRightStyles}
                      />
                    </span>
                  )}
                  {reviewUsername && (
                    // TODO change to decodo
                    <PostByLine
                      author={{
                        name: reviewUsername,
                        image: (
                          <div className='flex h-9 w-9 items-center justify-center rounded-full bg-gray-500'>
                            <Text color='base-white'>
                              {getSignatureFromFullname(reviewUsername)}
                            </Text>
                          </div>
                        ),
                      }}
                      releaseDate={
                        reviewDate
                          ? formatDate(new Date(reviewDate))
                          : undefined
                      }
                      classNames={{
                        releaseDate: 'text-gray-500',
                        authorName: 'text-gray-500',
                      }}
                    />
                  )}
                </SimpleCard>
              </div>
            )
          )}
        </BlockHeaderContent>
      </div>
    </BlockWrapper>
  );
}
